import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// next
import { useRouter } from 'next/router';
// @mui
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from "../../../hooks/useAuth";
import useResponsive from "../../../hooks/useResponsive";
// components
import Iconify from '../../../components/Iconify';
import SvgIconStyle from '../../../components/SvgIconStyle';
import Image from '../../../components/Image';
import Label from '../../../components/Label';
import {
  ContinueWorkHelpDialog
} from './';
// routes
import { PATH_JOBS } from '../../../routes/paths';
// utils
import axios from '../../../utils/axios';

// --------------------------------------------

const InlineColorText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ color, theme }) => ({
  color: color === "info" ? theme.palette.info.main : theme.palette.oldprimary.main,
}));

export default function ContinuePanel({
  coffeeChat, moveToMessageTab, reload, closedMatchesCount,
  isOpenContinueMessage, openContinueMessage, closeContinueMessage,
  isOpenContinueWork, openContinueWork, closeContinueWork,
}) {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');

  const { user } = useAuth();
  const userName = user?.username;
  const companyId = user?.employee?.company?.[0].id;
  const companyName = user?.employee?.company?.[0].name;
  const [message, setMessage] = useState("");
  const [workMessage, setWorkMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isOpenSendParttimeDialog, setIsOpenSendParttimeDialog] = useState(false);
  const [offerId, setOfferId] = useState(null);

  // help
  const [isOpenHelpDialog, setIsOpenHelpDialog] = useState(false);

  const initialWorkMessage = `${coffeeChat?.crew?.nickname} 크루님 안녕하세요.
지난번 커피챗 미팅했던 ${companyName}의 ${userName}입니다.
크루님께 짧은 업무 협업을 제안드립니다.

<요청 업무>
1. (업무 예시) 근무기록 기능 함께 기획
2. (업무 예시) 기획한 기능의 와이어프레이밍
3. (업무 예시) UI 디자인 (PC, Mobile 뷰)

업무 내용 간략히 전달드립니다.
확인해 주시고, 계약 조건 제안해 주시면 감사하겠습니다.`;

  const openHelpDialog = () => {
    setIsOpenHelpDialog(true);
  };

  const closeHelpDialog = () => {
    setIsOpenHelpDialog(false);
  };

  const openSendParttimeDialog = () => {
    setIsOpenSendParttimeDialog(true);
  };

  const closeSendParttimeDialog = () => {
    setIsOpenSendParttimeDialog(false);
  };

  const sendMessage = async () => {
    setIsSending(true);
    await axios.post(`/api/company/${companyId}/fliers/coffee/${coffeeChat.id}/message/`, {
      message,
    })
    setIsSending(false);
    moveToMessageTab();
  };

  const moveToOfferPage = (offerId) => {
    router.push({pathname: PATH_JOBS.root, query: {jid: offerId }})
  }

  const moveToNext = (needMeeting) => {
    if (needMeeting) {
      reload();
      moveToMessageTab();
    } else {
      moveToOfferPage(offerId);
    }
  }

  const sendWorkMessage = async (needMeeting) => {
    setIsSending(true);
    try {
      const response = await axios.post(`/api/company/${companyId}/fliers/coffee/${coffeeChat.id}/parttime/`, {
        message: workMessage,
        need_meeting: needMeeting,
      })
      setIsSending(false);
      const offerIdResponse = response.data.offer;
      setOfferId(offerIdResponse);
    } catch (error) {
      if (error.response && error.response.data && error.response.status === 400) {
        enqueueSnackbar(error.response.data, { variant: 'warning' });
      } else {
        enqueueSnackbar("요청이 실패하였습니다. 관리자에 문의해주세요.", { variant: 'warning' });
      }
      setIsSending(false);
    }
  };

  if (isOpenContinueWork) {
    return (
      <>
        {/* < 버튼 표시 */}
        <Box sx={{ display: "flex", mb: 2 }}>
          <IconButton color="inherit" edge="start" onClick={closeContinueWork}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "text.secondary" }} />
          </IconButton>
        </Box>
        <Typography variant="h5" gutterBottom>
          크루에게 업무 제안 메시지를 보내세요.
        </Typography>
        <Box
          sx={{
            p: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
            borderRadius: 1,
            mb: 2,
          }}
        >
          {closedMatchesCount > 0 ? (
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Label variant="filled" color="info">Tip</Label>
              <Stack spacing={.5}>
                <Typography variant={smUp ? "body2" : "caption"}>
                  이전에 업무 제안을 했던 크루입니다.
                </Typography>
                <Typography variant={smUp ? "body2" : "caption"}>
                  업무 제안은 <InlineColorText color="info"><b>여러번 다시 할 수 있어요.</b></InlineColorText>
                </Typography>
              </Stack>
            </Stack>
          ) : (
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Label variant="filled" color="info">Tip</Label>
            <Stack spacing={.5}>
              <Typography variant={smUp ? "body2" : "caption"}>
                예시처럼 <InlineColorText color="info"><b>요청할 업무 내용</b></InlineColorText>을 반드시 포함해 주세요.
              </Typography>
              <Typography variant={smUp ? "body2" : "caption"}>
                메시지를 보낸 후, <InlineColorText color="info"><b>미팅을 한번 더 제안</b></InlineColorText>할 수 있어요.
              </Typography>
            </Stack>
          </Stack>
          )}
        </Box>
        {!workMessage && (
        <Box
          sx={{
            borderRadius: 1,
            border: "1px solid grey",
            py: "16.5px",
            px: "14px",
            height: "355px",
            ":hover": {
              cursor: "pointer",
              border: "1px solid #000"
            }
          }}
          onClick={() => { if(!workMessage) setWorkMessage(initialWorkMessage);  }}
        >
          <Typography variant="body1" color="text.disabled" sx={{ lineHeight: "1.4357em"}}>
            {coffeeChat?.crew?.nickname} 크루님 안녕하세요.<br/>
            지난번 커피챗 미팅했던 {companyName}의 {userName}입니다.<br/>
            크루님께 짧은 업무 협업을 제안드립니다.<br/>
            <br/>
            &lt;요청 업무&gt;<br/>
            1. <u>(업무 예시) 근무기록 기능 함께 기획</u><br/>
            2. <u>(업무 예시) 기획한 기능의 와이어프레이밍</u><br/>
            3. <u>(업무 예시) UI 디자인 (PC, Mobile 뷰)</u><br/>
            <br/>
            업무 내용 간략히 전달드립니다.<br/>
            확인해 주시고, 계약 조건 제안해 주시면 감사하겠습니다.<br/>
          </Typography>
        </Box>
        )}
        {workMessage && (
          <TextField
            fullWidth
            multiline
            minRows={14}
            placeholder={initialWorkMessage}
            value={workMessage}
            onChange={(e) => setWorkMessage(e.target.value)}
            autoFocus
          />
        )}
        <Button
          sx={{ mt: 1 }}
          variant="text"
          color="error"
          startIcon={<SvgIconStyle src={`/icons/ic_headphone.svg`} sx={{ width: 28, height: 28, color: 'error.main' }} />}
          onClick={openHelpDialog}
          disableFocusRipple
        >
          업무 내용 작성이 어려우신가요?
        </Button>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: { xs: 3, sm: 6 } }}>
          <LoadingButton
            color="primary"
            variant="contained"
            size="large"
            onClick={openSendParttimeDialog} // sendWorkMessage
            sx={{ px: 6 }}
            disabled={!workMessage || (workMessage === initialWorkMessage)}
            loading={isSending}
          >
            보내기
          </LoadingButton>
        </Stack>
        <SendParttimeDialog
          isOpen={isOpenSendParttimeDialog}
          closeDialog={closeSendParttimeDialog}
          sendParttime={sendWorkMessage}
          moveToNext={moveToNext}
        />
        <ContinueWorkHelpDialog
          open={isOpenHelpDialog}
          closeDialog={closeHelpDialog}
          coffeeChat={coffeeChat}
        />
      </>
    )
  }


  if (isOpenContinueMessage) {
    return (
      <>
        {/* < 버튼 표시 */}
        <Box sx={{ display: "flex", mb: 2 }}>
          <IconButton color="inherit" edge="start" onClick={closeContinueMessage}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "text.secondary" }} />
          </IconButton>
        </Box>
        <Typography variant="h5" gutterBottom>
          크루에게 메시지를 보내보세요.
        </Typography>
        <Box
          sx={{
            p: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.oldprimary.main, 0.08),
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Label variant="filled" color="oldprimary">Tip</Label>
            <Stack spacing={.5}>
              <Typography variant={smUp ? "body2" : "caption"}>
                크루에게 커피챗에 대한 <InlineColorText><b>감사 인사</b></InlineColorText>를 보내보세요!
              </Typography>
              <Typography variant={smUp ? "body2" : "caption"}>
                더 궁금한 점이 생겼다면, <InlineColorText><b>질문</b></InlineColorText>을 해보는 것도 좋아요.
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={12}
          placeholder="전달할 메시지를 입력해주세요."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <LoadingButton
            color="primary"
            variant="contained"
            size="large"
            onClick={sendMessage}
            sx={{ px: 6 }}
            disabled={!message}
            loading={isSending}
          >
            보내기
          </LoadingButton>
        </Stack>
      </>
    )
  }

  return (
    <>
    <Box>
      <Typography variant="h5" gutterBottom>
        커피챗 한 크루와 네트워크를 이어가 보세요!
      </Typography>
      <Typography variant="body2">
        크루와 커피챗은 잘 마치셨나요?
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        앞으로의 채용 여정을 위해 소중한 인맥을 지속적으로 이어가 보세요.
      </Typography>

      <Card
        sx={{ backgroundColor: (theme) => alpha(theme.palette.oldprimary.main, 0.08), boxShadow: "none" }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={.5}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                <InlineColorText color="oldprimary">메시지</InlineColorText>를 보내보세요!
              </Typography>
              <Typography variant="caption">
                - 궁금한 점이 더 있다면, 메시지로 <b>질문</b>할 수 있어요.
              </Typography>
              <Typography variant="caption">
                - 커피챗에 대한 <b>감사 인사</b>를 전해보는 것도 좋아요.
              </Typography>
            </Stack>
            {smUp && (
              <Image
                alt="continue-message"
                src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_message.png"
                srcSet="
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_message.png 1x,
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_message_2x.png 2x,
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_message_3x.png 3x,
                "
                sx={{
                  width: 120,
                }}
              />
            )}
          </Stack>
        </CardContent>
        <Button color="oldprimary" size="large"
          endIcon={<Iconify icon="ic:chevron-right" />}
          fullWidth
          sx={{ 
            backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.12),
            borderRadius: 0,
            boxShadow: "none",
          }}
          onClick={openContinueMessage}
        >
          메시지 보내기
        </Button>
      </Card>

      <Card
        sx={{ mt: 2, mb: 5, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08), boxShadow: "none" }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={.5}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                <InlineColorText color="info">짧게 업무</InlineColorText>를 해보는것도 좋아요.
              </Typography>
              <Typography variant="caption">
                - 당장 이직 제안을 하기 고민되시나요?
              </Typography>
              <Typography variant="caption">
                &nbsp;&nbsp;&nbsp;<b>함께 일을 해보며 핏이 잘 맞는지 확인</b>해보세요.
              </Typography>
              <Typography variant="caption">
                - <b>프로젝트성 업무</b>를 크루에게 맡겨볼 수 있어요.
              </Typography>
            </Stack>
            {smUp && (
              <Image
                alt="continue-message"
                src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_work.png"
                srcSet="
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_work.png 1x,
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_work_2x.png 2x,
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/fliers/coffee_chat_continue_work_3x.png 3x,
                "
                sx={{
                  width: 120,
                }}
              />
            )}
          </Stack>
        </CardContent>
        <Button color="info" size="large"
          endIcon={<Iconify icon="ic:chevron-right" />}
          fullWidth
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.12),
            borderRadius: 0,
            boxShadow: "none",
          }}
          onClick={(e) => { e.stopPropagation(); openContinueWork(); }}
        >
          업무 제안하기
        </Button>
      </Card>
    </Box>
    </>
  )
}


function SendParttimeDialog({ isOpen, closeDialog, sendParttime, moveToNext }) {
  const smUp = useResponsive('up', 'sm');
  const [isLoading, setIsLoading] = useState(false);
  const [sendDone, setSendDone] = useState(false);  // 업무 제안 바로 보내기
  const [requestDone, setRequestDone] = useState(false);  // 미팅 요청하기

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      setSendDone(false);      
      setRequestDone(false);      
    }
  }, [isOpen]);

  const apply = async (needMeeting) => {
    try {
      setIsLoading(true);
      await sendParttime(needMeeting);

      if (needMeeting) setRequestDone(true);
      else setSendDone(true);

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="xs">
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
        </Stack>
        {sendDone ? (
          <Typography
            variant={smUp ? "h5" : "subtitle1"}
            gutterBottom
          >
            크루에게 업무 제안을 바로 보냈어요.
          </Typography>
        ) : requestDone ? (
          <Typography
            variant={smUp ? "h5" : "subtitle1"}
            gutterBottom
          >
            크루에게 미팅 요청을 보냈어요.
          </Typography>
        ) : (
          <Typography
            variant={smUp ? "h5" : "subtitle1"}
            gutterBottom
          >
            크루에게 업무 제안을 바로 보낼까요?
          </Typography>
        )}
        {sendDone ? (
          <Typography variant={smUp ? "body1" : "body2"}>
            크루가 계약 조건(인건비, 업무 시간 등)을 제안하면 알려드릴게요.
          </Typography>
        ) : requestDone ? (
          <Typography variant={smUp ? "body1" : "body2"}>
            크루가 미팅 가능한 시간을 보내면 알려드릴게요. 
          </Typography>
        ) : (
          <Typography variant={smUp ? "body1" : "body2"}>
            계약 진행 전, 크루와 미팅이 한번 더 필요하다면<br/>
            [크루에게 미팅 요청하기]를 선택해 주세요.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
          {!sendDone && !requestDone && (
            <LoadingButton
              loading={isLoading}
              onClick={() => apply(true)}
              size="large"
              fullWidth
              variant="outlined"
            >
              크루에게 미팅 요청하기
            </LoadingButton>
          )}
          {!sendDone && !requestDone && (
            <LoadingButton
              loading={isLoading}
              variant="contained"
              onClick={() => apply(false)}
              size="large"
              fullWidth
            >
              업무 제안 바로 보내기
            </LoadingButton>
          )}
          {(sendDone || requestDone) && (
            <LoadingButton
              loading={isLoading}
              variant="contained"
              onClick={() => moveToNext(requestDone)}
              size="large"
              sx={{ px: 4 }}
              fullWidth={!smUp}
            >
              확인했어요
            </LoadingButton>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
